import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { ApiError } from "@/lib/api";
import { authClient, useSession } from "@/lib/auth-client";

interface LoginCredentials {
  email: string;
  password: string;
}

type SessionUser = NonNullable<ReturnType<typeof useSession>["data"]>["user"];

export interface AuthContextValue {
  authenticated: boolean;
  isLoading: boolean;
  isLoggingIn: boolean;
  isLoggingOut: boolean;
  user: SessionUser | null;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const session = useSession();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const value = useMemo<AuthContextValue>(
    () => ({
      authenticated: session.data != null,
      isLoading: session.isPending,
      isLoggingIn,
      isLoggingOut,
      user: session.data?.user ?? null,
      login: async ({ email, password }) => {
        setIsLoggingIn(true);
        try {
          const res = await authClient.signIn.email({ email, password });
          if (res.error) {
            throw new ApiError(
              res.error.status ?? 400,
              res.error,
              res.error.message ?? "Login failed",
            );
          }
        } finally {
          setIsLoggingIn(false);
        }
      },
      logout: async () => {
        setIsLoggingOut(true);
        try {
          await authClient.signOut();
          queryClient.clear();
        } finally {
          setIsLoggingOut(false);
        }
      },
    }),
    [session.data, session.isPending, isLoggingIn, isLoggingOut, queryClient],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
